import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/SEO';
import { PageHero, Tabs, Icon, Badge, Button, Skeleton, EmptyState } from '../../components/ui';
import { festivalsService } from '../../services';

const FESTIVAL_IMAGES = [
  '/images/ghats/naya-ghat-2.jpg',
  '/images/design/ram-mandir-night.webp',
  '/images/ghats/saryu-ghat-1.jpeg',
  '/images/design/hero-ram-mandir.webp',
  '/images/hanuman/temple-1.webp',
  '/images/kanak-bhawan/temple.jpg',
];

const TABS = [
  { id: 0, label: 'All Festivals', icon: 'sparkles' },
  { id: 1, label: 'Upcoming', icon: 'calendar-days' },
  { id: 2, label: 'This Month', icon: 'calendar' },
  { id: 3, label: 'Popular', icon: 'star' },
];

function formatDate(dateString) {
  if (!dateString) return 'Date TBA';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return 'Date TBA';
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function Festivals() {
  const { t } = useTranslation();
  const [festivals, setFestivals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reload, setReload] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTab, setSelectedTab] = useState(0);
  const [openId, setOpenId] = useState(null);

  const fetchFestivals = useCallback(async () => {
    try {
      setLoading(true); setError(false);
      let data;
      switch (selectedTab) {
        case 1:
          data = await festivalsService.getUpcomingFestivals();
          break;
        case 2: {
          const now = new Date();
          data = await festivalsService.getFestivalsByMonth(now.getMonth() + 1, now.getFullYear());
          break;
        }
        default:
          data = await festivalsService.getAllFestivals();
      }
      setFestivals((data || []).slice().sort((a, b) => (b.ranking || 0) - (a.ranking || 0)));
    } catch {
      setError(true);
      setFestivals([]);
    } finally {
      setLoading(false);
    }
  }, [selectedTab]);

  useEffect(() => { fetchFestivals(); }, [fetchFestivals, reload]);

  let filtered = festivals;
  if (searchQuery) {
    const q = searchQuery.toLowerCase();
    filtered = festivals.filter((f) =>
      f.name?.toLowerCase().includes(q)
      || f.sanskrit_name?.toLowerCase().includes(q)
      || f.description?.toLowerCase().includes(q));
  }
  if (selectedTab === 3) {
    filtered = [...filtered].sort((a, b) => (b.popularity_score || 0) - (a.popularity_score || 0));
  }

  const schema = filtered.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Festivals of Ayodhya',
    url: 'https://ayodhyadham.info/festivals',
    numberOfItems: filtered.length,
    itemListElement: filtered.slice(0, 15).map((f, i) => ({
      '@type': 'ListItem', position: i + 1,
      item: {
        '@type': 'Event', name: f.name,
        description: f.description,
        startDate: f.dates?.start_date,
        endDate: f.dates?.end_date,
        location: { '@type': 'Place', name: 'Ayodhya', address: { '@type': 'PostalAddress', addressLocality: 'Ayodhya', addressRegion: 'Uttar Pradesh', addressCountry: 'IN' } },
      },
    })),
  } : null;

  return (
    <>
      <SEO
        title="Festivals of Ayodhya"
        description="Ram Navami, Deepotsav, Sawan Jhula and the celebrations that light up Ayodhya — dates, rituals and participating temples."
        canonicalUrl="/festivals"
        keywords="Ayodhya festivals, Ram Navami, Deepotsav Ayodhya, Sawan Jhula Mela, Kartik Purnima"
        structuredData={schema}
      />
      <PageHero compact crumbs={[{ label: t('Home'), to: '/' }, { label: t('Festivals') }]} title={t('Festivals of Ayodhya')} subtitle={t('Ram Navami, Deepotsav, Sawan Jhula and the celebrations that light up the holy city')} />

      <div className="container listing-wrap">
        <div className="listing__toolbar">
          <Tabs variant="pill" items={TABS.map((x) => ({ ...x, label: t(x.label) }))} value={selectedTab} onChange={setSelectedTab} ariaLabel={t('Festival filter')} />
          <div className="listing__tools">
            <label className="search">
              <Icon name="search" size={16} />
              <span className="sr-only">{t('Search festivals')}</span>
              <input type="search" placeholder={t('Search festivals…')} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
            </label>
          </div>
        </div>

        {loading ? (
          <div className="card-grid card-grid--wide">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} height={380} radius="var(--r-lg)" />)}</div>
        ) : error ? (
          <EmptyState icon="circle-alert" title={t('Could not load festivals')} body={t('The server is not reachable right now. Please try again.')} action={<Button variant="outline" onClick={() => setReload((k) => k + 1)}>{t('Retry')}</Button>} />
        ) : filtered.length === 0 ? (
          <EmptyState icon="sparkles" title={t('No festivals found')} body={t('Try another tab or clear your search.')} action={<Button variant="outline" onClick={() => { setSearchQuery(''); setSelectedTab(0); }}>{t('View all festivals')}</Button>} />
        ) : (
          <div className="card-grid card-grid--wide">
            {filtered.map((f, i) => {
              const open = openId === f.id;
              return (
                <article key={f.id || f.name} className={`card festival-card${open ? ' is-open' : ''}`}>
                  <div className="card__media card__media--wide">
                    <img src={f.images?.[0] || f.image || FESTIVAL_IMAGES[i % FESTIVAL_IMAGES.length]} alt={f.name} loading="lazy"
                      onError={(e) => { e.currentTarget.src = FESTIVAL_IMAGES[i % FESTIVAL_IMAGES.length]; }} />
                    <Badge variant="gold" className="festival-card__date"><Icon name="calendar" size={13} />{formatDate(f.dates?.start_date)}</Badge>
                    {f.popularity_score > 8 && <Badge variant="live" className="festival-card__hot">{t('Popular')}</Badge>}
                  </div>

                  <div className="card__body">
                    <h2 className="h-card">{f.name}</h2>
                    {f.sanskrit_name && <div className="festival-card__sanskrit hindi">{f.sanskrit_name}</div>}
                    <p className={open ? 'festival-card__desc is-full' : 'festival-card__desc'}>{f.description}</p>

                    <ul className="place-card__meta">
                      {f.dates?.duration_days && <li><Icon name="clock" size={14} /><span>{t('{{n}} days', { n: f.dates.duration_days })}</span></li>}
                      {f.dates?.lunar_calendar && <li><Icon name="moon" size={14} /><span>{f.dates.lunar_calendar}</span></li>}
                      {f.participating_temples?.length > 0 && <li><Icon name="temple" size={14} /><span>{t('{{n}} temples', { n: f.participating_temples.length })}</span></li>}
                    </ul>

                    {open && (
                      <div className="festival-card__more">
                        {f.significance && (
                          <>
                            <div className="festival-card__sub">{t('Significance')}</div>
                            <p className="festival-card__text">{f.significance}</p>
                          </>
                        )}
                        {f.celebrations?.length > 0 && (
                          <>
                            <div className="festival-card__sub">{t('Celebrations & rituals')}</div>
                            <ul className="check-list">
                              {f.celebrations.map((c, ci) => (
                                <li key={ci}><Icon name="circle-check" size={16} />{typeof c === 'string' ? c : c.name || c.description}</li>
                              ))}
                            </ul>
                          </>
                        )}
                        {f.participating_temples?.length > 0 && (
                          <>
                            <div className="festival-card__sub">{t('Participating temples')}</div>
                            <div className="info-card__tags">
                              {f.participating_temples.map((tp, ti) => (
                                <Badge key={ti} variant="outline">{typeof tp === 'string' ? tp : tp.name}</Badge>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    <button type="button" className="link-arrow festival-card__toggle" onClick={() => setOpenId(open ? null : f.id)} aria-expanded={open}>
                      {open ? t('Show less') : t('Read more')}
                      <Icon name={open ? 'chevron-up' : 'chevron-down'} size={15} />
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
